import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useWatch } from 'react-hook-form';
import { useMountedState, useLocalStorage } from 'react-use';
import { deviceToProductTypeDisplayString } from 'common/utils/helpers';
import { IconLoader } from 'common/IconLoader';
import { getSingleDevice } from 'redux/actions';
import { IconAndText } from 'components/shared/SvgAF/SvgAF';
import { deviceTypeToIcon } from 'components/features/DeviceView/DeviceAlarms/utils';
import ChartExplorerPageSearchBar from './ChartExplorerPageSearchBar';
import ChartExplorerPageRecentViewedDeviceMenu from './ChartExplorerPageRecentViewedDeviceMenu';
import styles from './ChartExplorerPageSearchContainer.module.css';

const DEVICE_ID_PLACEHOLDER = '0000000000000000';
const RECENT_VIEWED_DEVICES_NAME = 'recentViewedDevices';

function ChartExplorerPageSearchContainer() {
  const dispatch = useDispatch();
  const history = useHistory();
  const isMounted = useMountedState();
  const devices = useWatch({ name: 'state.query.devices' });
  const [deviceId] = devices ?? [];
  const [device, setDevice] = useState(null);
  const [recentViewedDevices, setRecentViewedDevices] = useLocalStorage(RECENT_VIEWED_DEVICES_NAME, []);
  const deviceType = useMemo(() => deviceToProductTypeDisplayString(device), [device]);
  const handleSelect = useCallback(
    (value) => {
      const {
        location: { pathname, search }
      } = history;
      const searchParams = new URLSearchParams(search);

      if (searchParams.get('deviceId') !== value) {
        searchParams.set('deviceId', value);
        history.replace({ pathname, search: searchParams.toString() });
      }
    },
    [history]
  );

  useEffect(() => {
    if (deviceId || recentViewedDevices.length === 0) {
      return;
    }

    const [recentViewedDevice] = recentViewedDevices;
    handleSelect(recentViewedDevice.id);
  }, [handleSelect, deviceId, recentViewedDevices]);

  useEffect(() => {
    if (!deviceId) {
      return;
    }

    dispatch(
      getSingleDevice(deviceId, (result) => {
        if (isMounted()) {
          setDevice(result);
        }
      })
    );
  }, [dispatch, isMounted, deviceId]);

  useEffect(() => {
    if (!device) {
      return;
    }

    const index = recentViewedDevices.findIndex((recentViewedDevice) => recentViewedDevice.id === device.id);

    if (index === -1) {
      setRecentViewedDevices([device, ...recentViewedDevices.slice(0, 9)]);
    } else if (index > 0) {
      setRecentViewedDevices([
        device,
        ...recentViewedDevices.slice(0, index),
        ...recentViewedDevices.slice(index + 1, 9)
      ]);
    }
  }, [device, recentViewedDevices, setRecentViewedDevices]);

  return (
    <div className={styles.container}>
      <div className={styles.deviceInfoBox}>
        <div className={styles.deviceInfoHeader}>DEVICE</div>
        <div className={styles.deviceInfoBody}>
          {deviceId ? (
            <IconAndText
              customClass={styles.iconAndText}
              img={deviceTypeToIcon[deviceType] || IconLoader.icon_donor}
              text={deviceId}
            />
          ) : (
            <IconAndText
              customClass={styles.iconAndTextPlaceholder}
              img={IconLoader.icon_device_type_placeholder}
              text={DEVICE_ID_PLACEHOLDER}
            />
          )}
          <ChartExplorerPageRecentViewedDeviceMenu onChange={handleSelect} options={recentViewedDevices} />
        </div>
      </div>
      <div className={styles.searchBox}>
        <ChartExplorerPageSearchBar onChange={handleSelect} />
      </div>
    </div>
  );
}

export default ChartExplorerPageSearchContainer;
